import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SurveyAnswerService } from 'app/shared/surveys/survey-answer.service';
import { DialogRef } from '../modal/dialog-ref';
import { DIALOG_DATA } from '../modal/dialog-tokens';
import { CoreLibModalComponent } from '../modal/modal.component';
import { ReasonsList } from 'app/shared/model';
import { Store } from '@ngxs/store';
import { DefaultLanguageState } from 'app/states/default-language/default-language.state';
import { AlertService } from 'app/core-lib/services/alerts/alert.service';
import { UtilService } from 'app/shared/util.service';
import { DateTime } from 'luxon';
import { MANAGE_WINDOW_EXPIRED_ERROR, getAnsweredSurveyErrorMessage } from 'app/shared/surveys/answered-survey-window';

const ALREADY_EXCLUDED_ERROR = 'Answered survey is already deleted';

@Component({
    selector: 'mh-core-lib-confirmation-modal',
    templateUrl: './confirmation-modal.component.html',
    styleUrls: ['./confirmation-modal.component.scss'],
    standalone: false
})
export class CoreLibConfirmationModalComponent extends CoreLibModalComponent implements OnInit {
  survey: any;
  deleteReasonList: ReasonsList;
  deleteForm: FormGroup;
  sending = false;

  constructor(
    protected dialogRef: DialogRef,
    @Inject(DIALOG_DATA) public data: any,
    private surveyAnswerService: SurveyAnswerService,
    private fb: FormBuilder,
    private store: Store,
    private alertService: AlertService,
    private utilService: UtilService,
  ) {
    super(dialogRef, data);
  }

  ngOnInit(): void {
    this.survey = this.data.survey;
    if (this.data.deleteReasonList) {
      const langId =
        this.store.selectSnapshot(DefaultLanguageState.language)?.languageId || this.utilService.getLanguageId();
      this.deleteReasonList = this.data.deleteReasonList.filter((reason) => reason.language_id === langId);
    }
    this.deleteForm = this.fb.group({
      reason: [null, Validators.required],
      reasonComment: [null],
    });
  }

  handleExclude() {
    if (this.deleteForm.invalid || this.sending) return;
    this.sending = true;
    const body = {
      reason_id: this.deleteForm.value.reason,
      reason_comment: this.deleteForm.value.reasonComment?.trim() || null,
    };

    this.surveyAnswerService.excludeAnswerSurvey(this.answeredSurvey.id, body).subscribe({
      next: () => {
        this.notify('core-lib.dialog.confirmation.exclude_survey_notify', 'success');
        this.close({ excluded: true });
      },
      error: (error) => {
        this.sending = false;
        const message = getAnsweredSurveyErrorMessage(error);
        if (message === MANAGE_WINDOW_EXPIRED_ERROR) {
          this.notify('shared.survey.manage_window_tooltip', 'error');
          return;
        }
        if (message === ALREADY_EXCLUDED_ERROR) {
          this.notify('core-lib.dialog.confirmation.exclude_survey_already', 'error');
          this.close({ excluded: true });
          return;
        }
        this.notify('user_management.actions.error', 'error');
      },
    });
  }

  handleCancel() {
    this.close();
  }

  private notify(messageKey: string, type: 'success' | 'error') {
    const panelClass = type === 'success' ? 'snackbar-panel-success-user-config' : 'snackbar-panel-error';
    this.alertService.handleAlert(messageKey, 5000, 'bottom', 'end', panelClass);
  }

  get answeredSurvey() {
    return this.survey?.answered_survey;
  }

  get guestName() {
    return [this.survey?.guest?.name, this.survey?.guest?.lastname].filter(Boolean).join(' ');
  }

  get npsCategory(): string {
    return this.answeredSurvey?.nps?.nps || '';
  }

  get npsRating() {
    return this.answeredSurvey?.nps?.rating;
  }

  get score() {
    return this.answeredSurvey?.satisfaction?.score;
  }

  get productKey() {
    return `filters.products.options.${+this.data.productId === 2 ? 2 : 1}`;
  }

  get currentLanguage() {
    return this.utilService.getCurrentLanguage();
  }

  get affectedMonth() {
    const answered = DateTime.fromSQL(this.answeredSurvey?.answered_date || '');
    if (!answered.isValid) return '';
    return answered.setLocale(this.currentLanguage).toFormat('LLLL yyyy');
  }

  get hasCases() {
    return this.answeredSurvey?.cases?.length > 0;
  }

  get isContacted() {
    return !!this.answeredSurvey?.managed;
  }
}
